/**
 * Adapts the browser's platform `fetch` to the shared `FetchAdapter` contract.
 *
 * Why a wrapper (not a direct `window.fetch.bind(window)`): BUG43 added
 * `signal?: { readonly aborted: boolean }` to `FetchAdapter`'s `init`. Because
 * a function parameter is contravariant, the platform `fetch` — whose `init`
 * only accepts a full DOM `AbortSignal` — is NOT assignable to `FetchAdapter`,
 * which must accept the wider structural `{ readonly aborted: boolean }`. TS
 * rejects the direct assignment (TS2322).
 *
 * The wrapper closes the gap without weakening the shared type or touching
 * `packages/shared` (which stays DOM-free per CP1). It accepts the shared
 * `FetchAdapter` init shape and forwards it to the platform `fetch`. At runtime
 * the `signal` the `HttpClient` passes is always a real `AbortSignal` (minted
 * from `globalThis.AbortController` in the client), so the request stays bound
 * and BUG43's timeout still fires — the wrapper only reconciles the static
 * types, it does not change behavior.
 */

import type { FetchAdapter } from '@shopista/shared/api';

/** Wrap a platform `fetch` so it satisfies the shared `FetchAdapter` type. */
export function toFetchAdapter(platformFetch: typeof fetch): FetchAdapter {
  return (input, init) =>
    platformFetch(input, init as RequestInit | undefined);
}
